import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, Pencil, Trash2 } from 'lucide-react';
import { financialStatementApi } from '../../services/financialStatementApi';
import type { FSComment } from '../../types/financialStatement';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    company: string;
    year: number;
    lineNr: number;
    accountNr?: string;
    scopeLabel: string;
    currentUserEmail: string;
    canEdit: boolean;
    onCountChanged?: () => void;
}

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleString('de-CH', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function scopeBadge(level: string): string {
    switch (level) {
        case 'Line': return 'bg-blue-100 text-blue-700';
        case 'Account': return 'bg-green-100 text-green-700';
        case 'Transaction': return 'bg-purple-100 text-purple-700';
        default: return 'bg-gray-100 text-gray-700';
    }
}

export function CommentPanel({
    isOpen, onClose, company, year, lineNr, accountNr,
    scopeLabel, currentUserEmail, canEdit, onCountChanged,
}: Props) {
    const [comments, setComments] = useState<FSComment[]>([]);
    const [loading, setLoading] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editText, setEditText] = useState('');

    const loadComments = useCallback(async () => {
        setLoading(true);
        try {
            const result = await financialStatementApi.getComments({
                company,
                year,
                lineNr,
                accountNr,
                includeChildren: true,
            });
            setComments(result.comments);
        } catch (err) {
            console.error('Failed to load comments:', err);
        } finally {
            setLoading(false);
        }
    }, [company, year, lineNr, accountNr]);

    useEffect(() => {
        if (isOpen) loadComments();
    }, [isOpen, loadComments]);

    const handleCreate = async () => {
        if (!newComment.trim() || submitting) return;
        setSubmitting(true);
        try {
            await financialStatementApi.createComment({
                companyCode: company,
                year,
                lineNr,
                accountNr,
                commentText: newComment.trim(),
            });
            setNewComment('');
            await loadComments();
            onCountChanged?.();
        } catch (err) {
            console.error('Failed to create comment:', err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdate = async (commentId: number) => {
        if (!editText.trim()) return;
        try {
            await financialStatementApi.updateComment(commentId, editText.trim());
            setEditingId(null);
            await loadComments();
        } catch (err) {
            console.error('Failed to update comment:', err);
        }
    };

    const handleDelete = async (commentId: number) => {
        try {
            await financialStatementApi.deleteComment(commentId);
            await loadComments();
            onCountChanged?.();
        } catch (err) {
            console.error('Failed to delete comment:', err);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-y-0 right-0 z-[70] flex">
            <div className="fixed inset-0 bg-black/20" onClick={onClose} />
            <div className="relative ml-auto w-[480px] bg-white shadow-2xl flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">Comments</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{scopeLabel}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* New comment form */}
                {canEdit && (
                    <div className="border-b px-4 py-3">
                        <div className="flex gap-2">
                            <textarea
                                value={newComment}
                                onChange={e => setNewComment(e.target.value)}
                                placeholder="Add a comment..."
                                className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm resize-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                rows={2}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                        handleCreate();
                                    }
                                }}
                            />
                            <button
                                onClick={handleCreate}
                                disabled={!newComment.trim() || submitting}
                                className="self-end rounded-md bg-blue-600 p-2 text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                                <Send className="h-4 w-4" />
                            </button>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Ctrl+Enter to send</p>
                    </div>
                )}

                {/* Comment list */}
                <div className="flex-1 overflow-auto px-4 py-3 space-y-3">
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                        </div>
                    ) : comments.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-8">No comments yet.</p>
                    ) : (
                        comments.map(comment => (
                            <div key={comment.CommentId} className="rounded-lg border p-3 space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5">
                                        <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${scopeBadge(comment.ScopeLevel)}`}>
                                            {comment.ScopeLevel}
                                        </span>
                                        {comment.AccountNr && (
                                            <span className="text-xs text-gray-400">{comment.AccountNr}</span>
                                        )}
                                    </div>
                                    {canEdit && comment.CreatedBy === currentUserEmail && (
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => { setEditingId(comment.CommentId); setEditText(comment.CommentText); }}
                                                className="text-gray-400 hover:text-blue-600 p-0.5"
                                            >
                                                <Pencil className="h-3 w-3" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(comment.CommentId)}
                                                className="text-gray-400 hover:text-red-600 p-0.5"
                                            >
                                                <Trash2 className="h-3 w-3" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {editingId === comment.CommentId ? (
                                    <div className="space-y-1.5">
                                        <textarea
                                            value={editText}
                                            onChange={e => setEditText(e.target.value)}
                                            className="w-full rounded border border-gray-300 px-2 py-1 text-sm resize-none"
                                            rows={3}
                                        />
                                        <div className="flex gap-1.5">
                                            <button
                                                onClick={() => handleUpdate(comment.CommentId)}
                                                className="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
                                            >Save</button>
                                            <button
                                                onClick={() => setEditingId(null)}
                                                className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200"
                                            >Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.CommentText}</p>
                                )}

                                <p className="text-xs text-gray-400">
                                    {comment.CreatedBy} - {formatDate(comment.CreatedAt)}
                                    {comment.UpdatedAt && ' (edited)'}
                                </p>
                            </div>
                        ))
                    )}
                </div>

            </div>
        </div>,
        document.body
    );
}
